import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { RoundState } from '@polyglot/engine';
import { Spinner } from '../components/ui';
import type { LocalDriver } from '../game/driver';
import { GameScreen } from '../game/GameScreen';
import type { LocalSession } from '../game/localSession';
import { botProgress, resumeLocalSession } from '../game/localSession';

export function Play() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [loaded, setLoaded] = useState<
    { session: LocalSession; driver: LocalDriver } | null | 'none'
  >(null);

  useEffect(() => {
    let alive = true;
    resumeLocalSession()
      .then((r) => alive && setLoaded(r ?? 'none'))
      .catch(() => alive && setLoaded('none'));
    return () => {
      alive = false;
    };
  }, []);

  // Replace /play: the Android back button (and the browser's) then returns to the screen before
  // the game instead of bouncing off the finished round (seen on a real phone).
  const onFinished = useCallback(
    (_s: RoundState) => navigate('/results', { replace: true }),
    [navigate],
  );

  if (loaded === null) return <Spinner label={t('setup.loading')} />;
  if (loaded === 'none')
    return (
      <main className="page">
        <p>{t('play.noGame')}</p>
        <Link className="btn primary" href="/setup">
          {t('home.newGame')}
        </Link>
      </main>
    );
  const { session, driver } = loaded;
  if (session.player.status === 'finished') {
    navigate('/results', { replace: true });
    return null;
  }
  const opponent = session.setup.bot
    ? {
        label: t('bots.robotName', { level: t(`bots.${session.setup.bot.level}`) }),
        isBot: true,
        progress: () => botProgress(session, driver.now()),
      }
    : null;
  return (
    <GameScreen key={session.id} driver={driver} opponent={opponent} onFinished={onFinished} />
  );
}
